// Student-facing "MY DEVELOPMENT" — read-only. Shows how the student's OWN writing AND organization of
// thinking developed within THIS assignment (Beginning -> Final -> Development), plus a LONGITUDINAL
// record across the student's assignments. Reuses stored WSR/IST analyses only (no LLM). Restrained and
// non-gamified: no single/composite score, no badges/points/percentiles; numbers stay secondary.
import { useEffect, useState } from "react";
import { cMyDevelopment, cMyDevelopmentHistory } from "@/lib/api";
import { ArrowUp, ArrowRight, ArrowDown, Minus } from "lucide-react";
import DevelopmentReport from "@/components/DevelopmentReport";

const DIR = {
  developed: { Icon: ArrowUp, cls: "text-emerald-700" },
  more_organized: { Icon: ArrowUp, cls: "text-emerald-700" },
  same: { Icon: ArrowRight, cls: "text-stone-600" },
  lower: { Icon: ArrowDown, cls: "text-amber-700" },
  less_organized: { Icon: ArrowDown, cls: "text-amber-700" },
  insufficient: { Icon: Minus, cls: "text-stone-400" },
};

const MiniDir = ({ direction }) => {
  const meta = DIR[direction?.code] || DIR.insufficient;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[12px] font-medium ${meta.cls}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />{direction?.label || "Insufficient information"}
    </span>
  );
};

export function LongitudinalRecord({ minStarted = 2 }) {
  const [hist, setHist] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    cMyDevelopmentHistory().then((d) => live && setHist(d)).catch(() => live && setErr(true));
    return () => { live = false; };
  }, []);
  if (err || !hist) return null;
  const started = (hist.assignments || []).filter((a) => a.started);
  if (started.length < minStarted) return null; // the classroom page keeps its two-assignment threshold; the final report shows every valid point
  const single = started.length === 1;

  return (
    <section data-testid="longitudinal-record-container" className="mt-10 border-t border-stone-200 pt-8 space-y-4">
      <div>
        <p className="font-mono-panel text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-medium text-stone-400">Over time</p>
        <h3 className="mt-1 font-serif-display text-lg sm:text-xl font-medium text-stone-900">Your development across assignments</h3>
        <p className="mt-1 text-[13px] leading-relaxed text-stone-600">{single ? "One assignment so far — a single point, not a trend. A record over time builds as you complete more." : hist.note}</p>
      </div>

      <div data-testid="longitudinal-comparability-caveat" className="rounded-md border border-stone-200 bg-stone-50 p-3">
        <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-stone-400 mb-1">Reading this record</p>
        <p className="text-[12.5px] leading-relaxed text-stone-600">{hist.comparability_note}</p>
      </div>

      <div className="space-y-3">
        {started.map((a) => (
          <div key={a.assignment_id} data-testid={`longitudinal-assignment-card-${a.assignment_id}`} className="rounded-lg border border-stone-200 bg-white p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-[14px] font-medium text-stone-900">{a.title}</p>
              {a.course_title && <p className="font-mono-panel text-[10px] uppercase tracking-[0.14em] text-stone-400">{a.course_title}</p>}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-stone-400 mb-1">Writing · {hist.writing_headline_label}</p>
                {a.writing?.analyzed
                  ? <MiniDir direction={a.writing.direction} />
                  : <span className="text-[12px] text-stone-400">Not analysed yet</span>}
              </div>
              <div>
                <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-stone-400 mb-1">Organization of Thinking</p>
                {a.thinking?.analyzed
                  ? <MiniDir direction={a.thinking.direction} />
                  : <span className="text-[12px] text-stone-400">Not analysed yet</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function MyDevelopment({ assignmentId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    cMyDevelopment(assignmentId)
      .then((d) => live && setData(d))
      .catch((e) => live && setErr(e?.response?.status === 404 ? "not_started" : "error"));
    return () => { live = false; };
  }, [assignmentId]);

  if (err === "not_started") return null;
  if (err) return <p data-testid="mydev-error" className="text-[13px] text-stone-500">Your development view isn&rsquo;t available right now.</p>;
  if (!data) return <p data-testid="mydev-loading" className="text-[13px] text-stone-400">Loading your development…</p>;

  return (
    <section data-testid="my-development" aria-labelledby="mydev-heading" className="mt-8">
      <div className="border-b border-stone-200 pb-4 mb-6">
        <p className="font-mono-panel text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-medium text-stone-400">
          {data.in_progress ? "Development so far" : "My development"} · {data.iteration_count} version{data.iteration_count === 1 ? "" : "s"}
        </p>
        <h2 id="mydev-heading" className="mt-1 font-serif-display text-2xl sm:text-3xl font-semibold tracking-tight text-stone-900">My Development</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-stone-600">
          How your writing and the organization of your thinking developed. These describe your development — they are not grades.
        </p>
      </div>

      <DevelopmentReport data={data} />
      <LongitudinalRecord />
    </section>
  );
}
